import React from "react"
import { Box, Typography, useTheme } from "@mui/material"
import dayjs from "dayjs"
import ChartCard from "../../molecules/charts/ChartCard"
import BarChart from "../../molecules/charts/BarChart"
import ChartAlert from "../../molecules/charts/ChartAlert"
import { colors } from "../../../utils/theme"
import { ClientFragment } from "../../../generated/graphql"
import {
  getMoodChartIndexDate,
  getReportedMoodDays,
  getMoodTrend,
} from "../../../utils/moodChart"

type MoodSeriesKey = "enjoyment" | "cravings"

type MoodTrendSeriesDatum = {
  dateKey: string
  value: number | null
}

/**
 * Single mood-series bar chart used by Loop Summary mood trends.
 * Renders thin monochrome bars on a 1–5 scale for the trailing reported week.
 */
export default function MoodTrendSeriesChart({
  data,
  series,
  title,
  scaleLegend,
  trendMessage,
  weekOffset = 0,
}: {
  data: ClientFragment["mood_measurements"]
  series: MoodSeriesKey
  title: string
  scaleLegend: string
  trendMessage: string
  weekOffset?: number
}) {
  const theme = useTheme()
  const chartIndexDate = getMoodChartIndexDate(weekOffset)
  const trailingMoodDays = getReportedMoodDays(chartIndexDate, 14, data)

  const thisWeekDays = trailingMoodDays.slice(7)
  const previousWeekDays = trailingMoodDays.slice(0, 7)

  const chartData: MoodTrendSeriesDatum[] = thisWeekDays.map((datum) => ({
    dateKey: datum.date.format("YYYY-MM-DD"),
    value: datum[series] ?? null,
  }))

  const thisWeekValues = thisWeekDays
    .map((datum) => datum[series])
    .filter((value): value is number => typeof value === "number")

  const previousWeekValues = previousWeekDays
    .map((datum) => datum[series])
    .filter((value): value is number => typeof value === "number")

  const trend = getMoodTrend(thisWeekValues, previousWeekValues)

  const xTickFormatter = (tick: string) => {
    const day = tick.slice(-2)
    return day.startsWith("0") ? day.slice(1) : day
  }

  const barColor =
    theme.palette.mode === "dark"
      ? theme.palette.vira?.ink ?? "#FFFFFF"
      : colors.neutral.denim

  const alertComponent = (): React.ReactNode => {
    if (!trend || trend.direction === "no_change") {
      return null
    }

    // Cravings: decrease is clinically positive (matches CravingsChart / VIRA-3377).
    const isPositive =
      series === "cravings"
        ? trend.direction === "decreasing"
        : trend.direction === "increasing"

    return (
      <ChartAlert
        association={isPositive ? "positive" : "negative"}
        variant={trend.direction}
        changeAmount={Number(trend.meanChange.toFixed(1))}
        message={trendMessage}
      />
    )
  }

  const Alert = alertComponent()

  return (
    <ChartCard
      title={title}
      footer={
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1.5,
            flexWrap: "wrap",
            minWidth: 0,
          }}
        >
          <Typography
            sx={(t) => ({
              flex: "1 1 140px",
              minWidth: 0,
              color: t.palette.vira?.muted ?? t.palette.text.secondary,
              fontSize: 12,
              lineHeight: "16px",
            })}
          >
            {scaleLegend}
          </Typography>
          {Alert ? (
            <Box
              sx={{
                flex: "1 1 auto",
                minWidth: 0,
                maxWidth: "100%",
                ml: "auto",
              }}
            >
              {Alert}
            </Box>
          ) : null}
        </Box>
      }
    >
      <Box
        sx={{
          width: "100%",
          maxWidth: "100%",
          minWidth: 0,
          overflow: "hidden",
        }}
      >
        <BarChart
          data={chartData}
          xKey="dateKey"
          yKey="value"
          maxY={5}
          yTicks={[1, 3, 5]}
          yTickFormatter={(value) => value.toString()}
          xTickFormatter={xTickFormatter}
          getBarColor={() => barColor}
          getTooltipLabel={(datum) => {
            if (datum.value == null) {
              return undefined
            }
            const dayLabel = dayjs(datum.dateKey).format("MMM D")
            return `${title}: ${datum.value}\n${dayLabel}`
          }}
          chartPadding={{ top: 16, right: 8, bottom: 28, left: 28 }}
          height={150}
          width={420}
          responsive
          barWidth={10}
          cornerRadius={2}
        />
      </Box>
    </ChartCard>
  )
}
